var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from "react";
import { format, isAfter, addMonths, subMonths } from "date-fns";
import { DayPicker } from "react-day-picker";
import { Popper } from "../../Popper";
import { CloseIcon } from "../../Icons/CloseIcon";
import { DateRangeIcon } from "../../Icons/DateRangeIcon";
import { getTopDate, getFirstDate, isSameMonth, isSameDate, dateToNumber, numberToDate } from "../utils";
import { ITEM_TEXT_FORMAT, MonthMenu } from "../MonthMenu";
import { ContainerRangeDatePickerStyle, InputRangeDatePickerStyle } from "./styled";
import { useOutsideClick } from "../../../internal-hooks/useOutsideClick";
function isValueProps(x) {
    return "value" in x;
}
var EMPTY_RANGE = {
    from: undefined,
    to: undefined
};
var RangeDatePicker = function (props) {
    var id = props.id, className = props.className, style = props.style;
    var range = isValueProps(props) ? props.value : props.range;
    var onRangeChange = isValueProps(props)
        ? props.onChange
        : props.onRangeChange;
    var DEFAULT_DATE = getFirstDate(new Date());
    var _a = __read(React.useState(null), 2), containerEl = _a[0], setContainerEl = _a[1];
    var pickerRef = React.useRef(null);
    var _b = __read(React.useState(undefined), 2), rangeLeft = _b[0], setRangeLeft = _b[1]; // manage leftRange
    var _c = __read(React.useState(undefined), 2), rangeRight = _c[0], setRangeRight = _c[1]; // manage rightRange
    var _d = __read(React.useState(DEFAULT_DATE), 2), currentLeftMonth = _d[0], setCurrentLeftMonth = _d[1];
    var _e = __read(React.useState(addMonths(DEFAULT_DATE, 1)), 2), currentRightMonth = _e[0], setCurrentRightMonth = _e[1];
    var _f = __read(React.useState(false), 2), open = _f[0], setOpen = _f[1];
    var toggleOpen = React.useCallback(function () {
        setOpen(!open);
    }, [open, setOpen]);
    var onClose = React.useCallback(function () {
        setOpen(false);
    }, []);
    useOutsideClick([pickerRef], onClose, open);
    // FIXME: Used during render.  Replace with a const
    var isRangeInsideSameSide = function () {
        var res = range.from &&
            range.to &&
            ((isAfter(currentRightMonth, numberToDate(range.to)) &&
                !isSameMonth(currentRightMonth, numberToDate(range.to))) ||
                (isAfter(range.from, currentLeftMonth) &&
                    !isSameMonth(numberToDate(range.from), currentLeftMonth)) ||
                isSameMonth(numberToDate(range.from), numberToDate(range.to)));
        return res ? res : false;
    };
    var inputValue = "";
    if (range.from && range.to) {
        inputValue = "".concat(format(range.from, "P"), " \u2014 ").concat(format(range.to, "P"));
    }
    React.useEffect(function () {
        if (open && pickerRef.current) {
            setWeekdaysCalendar(pickerRef.current);
        }
    }, [open]);
    React.useEffect(function () {
        if (range.from && range.to) {
            if (isRangeInsideSameSide()) {
                setRangeRight(EMPTY_RANGE);
                setRangeLeft(EMPTY_RANGE);
                if (isAfter(currentRightMonth, range.to)) {
                    setRangeLeft({
                        from: numberToDate(range.from),
                        to: numberToDate(range.to)
                    });
                }
                else {
                    setRangeRight({
                        from: numberToDate(range.from),
                        to: numberToDate(range.to)
                    });
                }
            }
            else {
                // paint from the selected Date to the last day of the Left month
                setRangeLeft({
                    from: numberToDate(range.from),
                    to: getTopDate(subMonths(range.to, 1))
                });
                // paint from the first day of the right month to the selected day
                setRangeRight({
                    from: getFirstDate(addMonths(range.from, 1)),
                    to: numberToDate(range.to)
                });
            }
        }
    }, [range.to, range.from]);
    var handleDayClick = function (day) {
        if (range.from &&
            range.to &&
            isSameDate(numberToDate(range.from), day) &&
            isSameDate(numberToDate(range.to), day)) {
            // triple-click should clean all
            clearAll();
            return;
        }
        if (range.from && range.to && isSameDate(numberToDate(range.from), day)) {
            // select twice "from" edge from Range, should clean range
            clearAll();
            return;
        }
        if (range.from && range.to && isSameDate(numberToDate(range.to), day)) {
            // select twice "to" edge from Range, should set to as new from and clean old to
            onRangeChange({
                to: undefined,
                from: range.to
            });
            return;
        }
        if (range.from && isAfter(range.from, day)) {
            // the selected day is before the "from" edge
            var from = range.to ? range.to : range.from;
            onRangeChange({
                from: dateToNumber(day),
                to: from
            });
            return;
        }
        if (range.from && isAfter(day, range.from)) {
            // the selected day is after the "from" edge
            onRangeChange({
                from: range.from,
                to: dateToNumber(day)
            });
            return;
        }
        if (range.from && !range.to && isSameDate(numberToDate(range.from), day)) {
            // double-click at the same day should set a range with same range edges
            onRangeChange({
                from: dateToNumber(day),
                to: dateToNumber(day)
            });
            return;
        }
        if (!range.from && !range.to) {
            // none range, set the first element to the "from" edge
            onRangeChange({
                from: dateToNumber(day),
                to: undefined
            });
            return;
        }
    };
    var isFullyCharged = function () {
        return range.from != undefined && range.to != undefined;
    };
    var clearAll = function () {
        setRangeLeft(EMPTY_RANGE);
        setRangeRight(EMPTY_RANGE);
        onRangeChange(EMPTY_RANGE);
    };
    var setCalendarContainerClass = function () {
        if (isRangeInsideSameSide()) {
            return "calendar-container";
        }
        else {
            return "calendar-container diff";
        }
    };
    var getCaption = function (props) { return function (date) {
        return (React.createElement(React.Fragment, null,
            format(date, ITEM_TEXT_FORMAT),
            React.createElement(MonthMenu, __assign({}, props))));
    }; };
    return (React.createElement("div", { id: id, className: className, style: style },
        React.createElement(InputRangeDatePickerStyle, { ref: setContainerEl },
            React.createElement("div", { className: !open ? "calendar-input" : "calendar-input clicked" },
                React.createElement("input", { className: "calendar-text", type: "text", value: inputValue, readOnly: true, placeholder: "MM/DD/YYYY \u2014 MM/DD/YYYY", size: 30, onClick: toggleOpen }),
                React.createElement(DateRangeIcon, { className: !isFullyCharged() || open ? "icon" : "hidden", onClick: toggleOpen }),
                React.createElement(CloseIcon, { className: isFullyCharged() && !open ? "icon" : "hidden", onClick: clearAll }))),
        React.createElement(Popper, { isOpen: open, placement: "bottom-start", anchorEl: containerEl },
            React.createElement(ContainerRangeDatePickerStyle, { ref: pickerRef },
                React.createElement("div", { className: setCalendarContainerClass() },
                    React.createElement(DayPicker, { mode: "range", className: "left", selected: rangeLeft, defaultMonth: currentLeftMonth, month: currentLeftMonth, fromMonth: subMonths(currentLeftMonth, 1), toDate: getTopDate(subMonths(currentRightMonth, 1)), showOutsideDays: true, fixedWeeks: true, onSelect: setRangeLeft, onDayClick: handleDayClick, onMonthChange: setCurrentLeftMonth, formatters: {
                            formatCaption: getCaption({
                                date: currentLeftMonth,
                                handleMonthChange: setCurrentLeftMonth,
                                reverse: true
                            })
                        } }),
                    React.createElement(DayPicker, { mode: "range", className: "right", selected: rangeRight, defaultMonth: currentRightMonth, month: currentRightMonth, fromMonth: addMonths(currentLeftMonth, 1), toDate: addMonths(currentRightMonth, 1), showOutsideDays: true, fixedWeeks: true, onSelect: setRangeRight, onDayClick: handleDayClick, onMonthChange: setCurrentRightMonth, formatters: {
                            formatCaption: getCaption({
                                date: currentRightMonth,
                                handleMonthChange: setCurrentRightMonth
                            })
                        } }))))));
};
function getWeekCaption(day) {
    var dayOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    day = day > 6 ? day - 7 : day;
    return dayOfWeek[day];
}
// generates the weekdays caption
function setWeekdaysCalendar(parent) {
    var elements = parent.getElementsByClassName("rdp-head_cell");
    for (var i = 0; i < elements.length; i++) {
        var doc = document.createElement("h4");
        doc.textContent = getWeekCaption(i);
        doc.className = "calendar-weekday";
        elements[i].appendChild(doc);
    }
}
export default RangeDatePicker;
//# sourceMappingURL=RangeDatePicker.js.map